/**
 * PublicPortalLoginPage — `/c/<slug>/login` (no auth required)
 *
 * Branded per-portal login. Verifies against the workspace_members
 * password_hash (per-membership password model), not the global
 * users.password_hash. Dual-identity users can have a separate password
 * for each portal they're invited to.
 *
 * Falls back to the user's global password if their membership has no
 * portal-specific hash yet (legacy clients invited before per-membership
 * passwords shipped).
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { setSessionToken } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPortalLoginPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/portal/${slug}`);
        if (r.status === 404) { setNotFound(true); return; }
        if (!r.ok) throw new Error('failed');
        setBranding(await r.json());
      } catch {
        setNotFound(true);
      } finally {
        setBrandingLoading(false);
      }
    })();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/portal/${slug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || 'Login failed');
        setSubmitting(false);
        return;
      }
      // Persist the bearer token so the standard interceptor finds it.
      if (data.token) {
        setSessionToken(data.token);
      }
      // Force-route to portal regardless of dual-identity state — they
      // explicitly logged in via the branded portal.
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (notFound || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold mb-3">Portal not found</h1>
          <p className="text-zinc-600 mb-6">
            We couldn't find a client portal at this address. Double-check the link your firm sent you.
          </p>
          <Link to="/" className="text-emerald-600 hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  const brand = branding.brand_color || '#10b981';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50 flex flex-col" data-testid="portal-login-page">
      {/* Branded header */}
      <header className="border-b" style={{ borderColor: `${brand}22` }}>
        <div className="max-w-5xl mx-auto w-full px-6 py-5 flex items-center justify-between">
          <Link to={`/c/${slug}`} className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ background: brand }}
              >
                {(branding.name || '?').charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold text-lg leading-none">{branding.name}</p>
              <p className="text-xs text-zinc-500 mt-1">Client Portal</p>
            </div>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Sign in to your portal</h1>
            <p className="text-sm text-zinc-600 mb-6">
              Use the password you set for <strong>{branding.name}</strong>'s portal.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-login-form">
              <div>
                <Label htmlFor="portal-email">Email</Label>
                <Input
                  id="portal-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="portal-login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="portal-password">Password</Label>
                  <Link
                    to={`/c/${slug}/forgot-password?email=${encodeURIComponent(email)}`}
                    className="text-xs font-medium hover:underline"
                    style={{ color: brand }}
                    data-testid="portal-login-forgot-link"
                  >
                    Forgot?
                  </Link>
                </div>
                <div className="relative mt-1">
                  <Input
                    id="portal-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                    data-testid="portal-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="portal-login-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
                  data-testid="portal-login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 text-base text-white shadow-md hover:shadow-lg transition-shadow"
                style={{ background: brand }}
                data-testid="portal-login-submit"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Sign in
              </Button>
            </form>

            <div className="mt-6 text-center text-xs text-zinc-500">
              Don't have access yet?{' '}
              {branding.public_signup_enabled ? (
                <Link
                  to={`/c/${slug}/start`}
                  className="font-medium hover:underline"
                  style={{ color: brand }}
                  data-testid="portal-login-signup-link"
                >
                  Sign up
                </Link>
              ) : (
                <span>Contact your firm directly.</span>
              )}
            </div>
            <div className="mt-3 text-center text-xs text-zinc-400">
              Not sure which firm invited you?{' '}
              <Link
                to={`/portals/find?email=${encodeURIComponent(email)}`}
                className="font-medium underline hover:text-zinc-600"
                data-testid="portal-login-lookup-link"
              >
                Find my portal
              </Link>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-zinc-400">
            Your password for this portal is independent of any other CountBucks accounts you may have.
          </p>
        </div>
      </main>

      <footer className="border-t bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 text-xs text-zinc-500 text-center">
          Powered by{' '}
          <a href="https://countbucks.com" className="font-semibold text-zinc-700 hover:underline">
            CountBucks
          </a>
        </div>
      </footer>
    </div>
  );
}
